import React, { Children, Component, ReactElement } from 'react';

import { OptionAPI } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { DataContext, RouteContext } from '@philips-emr/tws-core/lib/component/context';
import { arrayOf, bool, eventListener, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ClickEvent } from '@philips-emr/tws-core/lib/component/types';
import HistoryService from '@philips-emr/tws-core/lib/services/history-service';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { isFunction, trigger } from '@philips-emr/tws-utils';

import './option.css';

interface OptionProps {
  children?: string | string[];
  enabled: boolean;
  onBlur?: (event: FocusEvent) => void;
  onClick?: (event: ClickEvent) => void;
  onContextMenuClick?: (event: ClickEvent) => void;
  onDoubleClick?: (event: ClickEvent) => void;
  onFocus?: (event: FocusEvent) => void;
  preventDefault: boolean;
  route?: string;
  stopPropagation: boolean;
  value: string;
  visible: boolean;
}

@AwesomeComponent({ type: ComponentType.NAVIGATION, api: OptionAPI })
class Option extends Component<OptionProps> {

  static contextType = DataContext.Context;

  static propTypes = {
    children: oneOfType([string, arrayOf(string)]).notEnumerable(),
    enabled: bool,
    onBlur: eventListener,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onFocus: eventListener,
    preventDefault: bool.group('events'),
    route: string,
    stopPropagation: bool.group('events'),
    value: string,
    visible: bool,
  };

  static defaultProps = {
    enabled: true,
    value: 'Option',
    visible: true,
  };

  getTextValue = (): string => {
    const { children, value } = this.props;

    if (Children.count(children) === 1 && typeof children === 'string') {
      return msg(children);
    }

    return msg(value);
  };

  onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, routeContext: RouteContext | null): void => {
    const { route, onClick, preventDefault, stopPropagation } = this.props;

    if (preventDefault) {
      event.preventDefault();
    }

    if (stopPropagation) {
      event.stopPropagation();
    }

    if (route && routeContext) {
      const completeRoute = routeContext.nestedRoute(route);
      HistoryService.push(completeRoute, this.context);
    } else if (isFunction(onClick)) {
      onClick(new ClickEvent({
        preventDefault: () => event.preventDefault(),
        stopPropagation: () => event.stopPropagation(),
      }));
    }
  };

  render(): ReactElement | null {
    const { enabled, onBlur, onContextMenuClick, onDoubleClick, onFocus, visible } = this.props;

    if (!visible) {
      return null;
    }

    return (
      <RouteContext.Context.Consumer>
        {routeContext => (
          <button
            className="c-option"
            disabled={!enabled}
            onBlur={trigger(onBlur)}
            onContextMenu={trigger(onContextMenuClick)}
            onDoubleClick={trigger(onDoubleClick)}
            onFocus={trigger(onFocus)}
            onClick={(e): void => this.onClick(e, routeContext)}
          >
            {this.getTextValue()}
          </button>
        )}
      </RouteContext.Context.Consumer>
    );
  }
}

export default Option;
